import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { PricingConfigurator } from '@/components/PricingConfigurator'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { trackPricingView } from '@/lib/analytics'
import { Check, ArrowRight } from 'lucide-react'
import {
  CAPABILITIES,
  BUNDLE_RULE_LINE,
  MANAGED_SERVICES,
  DFY_BUNDLES,
  FESTIVAL_LANE,
  CONSULTING,
  ONE_TIME_AND_ADDONS,
  type Capability,
  type PriceBundle,
} from '@gjl/voice/pricing'

// Pricing data is single-sourced from @gjl/voice/pricing. Edit prices there.

// Catalog ids/names are internal; customer-facing labels can differ.
const DISPLAY_LABELS: Record<string, string> = {
  'bespoke-build': 'Custom build',
}

const labelFor = (item: { id: string; name: string }) => DISPLAY_LABELS[item.id] ?? item.name

// The four small conveniences render as fine print, not cards.
const CARD_BUILDS = new Set(['launch-build', 'bespoke-build'])

function CapabilityCard({ cap }: { cap: Capability }) {
  return (
    <motion.div
      className="package-card border-primary/50 flex flex-col rounded-lg border p-6 shadow-sm transition hover:shadow-md"
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xl font-semibold">{cap.name}</h3>
        <div className="text-primary text-lg font-bold whitespace-nowrap">
          ${cap.priceMonthly}/mo
          {cap.proMonthly ? (
            <span className="text-muted-foreground text-sm font-normal"> · Pro ${cap.proMonthly}</span>
          ) : null}
        </div>
      </div>
      {cap.perUnitFee && <div className="text-muted-foreground mt-1 text-sm">+ {cap.perUnitFee}</div>}
      <p className="text-muted-foreground mt-3 flex-1 text-sm">{cap.note}</p>
      <div className="mt-4 flex items-center gap-2 text-sm">
        {cap.selfServeLive ? (
          <>
            <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
            <span className="text-white/80">Run it yourself today</span>
          </>
        ) : (
          <>
            <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
            <span className="text-white/80">Our team sets it up and runs it for you today</span>
          </>
        )}
      </div>
    </motion.div>
  )
}

function BundleCard({ bundle, badge }: { bundle: PriceBundle; badge?: string }) {
  return (
    <motion.div
      className="package-card border-primary relative flex flex-col justify-between rounded-lg border p-6 shadow-sm transition hover:shadow-md"
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {badge && (
        <div className="bg-primary text-primary-foreground absolute -top-3 left-1/2 -translate-x-1/2 transform rounded-full px-3 py-1 text-xs font-bold">
          {badge}
        </div>
      )}
      <div className="space-y-3">
        <h3 className="text-xl font-semibold">{labelFor(bundle)}</h3>
        <div className="text-primary text-lg font-bold">{bundle.price}</div>
        <p className="text-muted-foreground text-sm">{bundle.note}</p>
      </div>
    </motion.div>
  )
}

export default function PackagesPage() {
  useEffect(() => {
    trackPricingView('/packages')
  }, [])
  // SPA navigation doesn't trigger native anchor scrolling (e.g. /packages#consulting),
  // including hash-only changes while already on this page.
  const location = useLocation()
  useEffect(() => {
    const id = location.hash.slice(1)
    if (!id) return
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }, [location])
  const breadcrumbs = generateBreadcrumbs('/packages', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const capabilitiesRef = useStaggeredGrid<HTMLDivElement>('.package-card', { stagger: 0.08 })
  const dfyRef = useStaggeredGrid<HTMLDivElement>('.package-card', { stagger: 0.08 })
  const festivalRef = useStaggeredGrid<HTMLDivElement>('.package-card', { stagger: 0.1 })
  const addonsRef = useStaggeredGrid<HTMLDivElement>('.package-card', { stagger: 0.06 })
  const howItWorksRef = useScrollReveal<HTMLDivElement>('.step-item', { stagger: 0.12 })

  useSEO({
    title: 'Pricing',
    description:
      'Pay for the capabilities you turn on: website, email, donor management, ticketing, and music planning. Run them yourself from $25/mo, or have Fugue Lab run them for you.',
    url: '/packages',
    canonical: '/packages',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'OfferCatalog',
      name: 'Fugue Lab Pricing',
      description: 'Modular platform pricing for music organizations: self-serve and done-for-you.',
      provider: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="mx-auto space-y-16">
      {/* Hero */}
      <section className="relative min-h-[80vh] w-full overflow-hidden md:h-[70vh]">
        <img
          src="/servers.jpg"
          alt="The Fugue Lab platform for music organizations"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div
          ref={heroRef}
          className="relative z-10 mx-auto flex min-h-[80vh] max-w-5xl flex-col items-center justify-center gap-y-6 px-8 text-center text-white"
        >
          <h1 className="text-4xl font-bold tracking-tight">Everything your music organization needs online</h1>
          <p className="max-w-2xl text-lg leading-relaxed text-white/90">
            Website, email, donations, ticketing, and music planning on one platform. Turn on what you need. Run it
            yourself, or have us run it for you.
          </p>
          <div className="mt-2 flex flex-col gap-2 text-sm text-white/70">
            <div className="flex items-center justify-center gap-2">
              <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>Self-serve from $25/mo. Done-for-you from $100/mo.</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>Accepting donations is always free: 0% platform fee</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>No vendor lock-in. Leave any time with all of your data, free.</span>
            </div>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="mt-4 cursor-pointer">
                Book a Free Call →
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Two-axis explainer */}
      <section className="px-8 text-center">
        <div className="mx-auto max-w-3xl space-y-4">
          <h2 className="text-2xl font-bold">Two choices, one platform</h2>
          <p className="text-muted-foreground">
            Every organization makes two decisions: which capabilities to turn on, and who runs them. Start with a
            website, add email or giving when you are ready, and delegate any piece to us at any time. Everything runs
            on the same system, so upgrading never means migrating.
          </p>
        </div>
      </section>

      {/* Interactive configurator */}
      <PricingConfigurator />

      {/* Capabilities a la carte */}
      <section className="px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold">The capabilities</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Each capability is one flat monthly price. Website and Email Marketing are self-serve today; donor
              management, ticketing, and music planning are set up and run for you by our team while their self-serve
              controls roll out.
            </p>
          </div>
          <div ref={capabilitiesRef} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((cap) => (
              <CapabilityCard key={cap.id} cap={cap} />
            ))}
          </div>
          <p className="text-muted-foreground text-center text-sm">
            Accepting donations is free forever: a Donate button with money going straight to your own Stripe account,
            0% platform fee. The Donor Management price is for the management layer: recurring giving, tax receipts, and
            donor records.
          </p>
        </div>
      </section>

      {/* Bundle rule */}
      <section className="bg-gray-900/50 px-8 py-12">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <h2 className="text-2xl font-bold text-white">Bundle and save</h2>
          <p className="text-primary text-lg font-semibold">{BUNDLE_RULE_LINE}</p>
          <p className="text-sm text-white/60">
            Any mix counts. Pay annually and get 2 months free. Donor management, ticketing, and Music Manager are set
            up and run for you by our team today.
          </p>
        </div>
      </section>

      {/* Done for you */}
      <section className="px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold">Done for you</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              We operate it and you stay in charge. Every managed service includes the underlying software, so you see
              one number.
            </p>
          </div>
          <div ref={dfyRef} className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {MANAGED_SERVICES.map((svc) => (
              <motion.div
                key={svc.id}
                className="package-card border-primary/50 flex flex-col rounded-lg border p-6 shadow-sm transition hover:shadow-md"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <h3 className="text-xl font-semibold">{svc.name}</h3>
                <div className="text-primary mt-2 text-lg font-bold">${svc.priceMonthly}/mo</div>
                <p className="text-muted-foreground mt-3 flex-1 text-sm">{svc.note}</p>
              </motion.div>
            ))}
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {DFY_BUNDLES.map((bundle) => (
              <BundleCard key={bundle.id} bundle={bundle} />
            ))}
          </div>
          <p className="text-muted-foreground mx-auto max-w-2xl text-center text-sm">
            Done-for-you plans start with a one-time onboarding (a Launch build counts) and a 3-month minimum term.
            Managed Website requests are unlimited: one active at a time, 72-hour turnaround.
          </p>
        </div>
      </section>

      {/* Festival lane */}
      <section className="bg-gray-900/50 px-8 py-16">
        <div className="mx-auto max-w-5xl space-y-8">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold text-white">For festivals</h2>
            <p className="mx-auto max-w-2xl text-white/80">
              Auditions and applications with no per-applicant fees, on the same platform as your website. $500/mo if
              you have a designer, $1,000/mo if you would rather not hire one.
            </p>
          </div>
          <div ref={festivalRef} className="mx-auto grid max-w-xl gap-6">
            {FESTIVAL_LANE.map((bundle) => (
              <BundleCard key={bundle.id} bundle={bundle} badge="FESTIVALS" />
            ))}
          </div>
          <p className="text-center text-sm text-white/60">
            Want the festival fully run, branding and launch included? That is Concierge, above.
          </p>
        </div>
      </section>

      {/* Consulting */}
      <section id="consulting" className="px-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold">Consulting</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Strategy and direction without full execution: done with you, above self-serve and distinct from
              done-for-you.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {CONSULTING.map((bundle) => (
              <BundleCard key={bundle.id} bundle={bundle} />
            ))}
          </div>
        </div>
      </section>

      {/* Builds + add-ons */}
      <section className="px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold">Builds and add-ons</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Get live with a one-time build, then add the small conveniences that keep everything running under one
              roof.
            </p>
          </div>
          <div ref={addonsRef} className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
            {ONE_TIME_AND_ADDONS.filter((item) => CARD_BUILDS.has(item.id)).map((item) => (
              <BundleCard key={item.id} bundle={item} />
            ))}
          </div>
          <div className="mx-auto max-w-2xl space-y-1">
            {ONE_TIME_AND_ADDONS.filter((item) => !CARD_BUILDS.has(item.id)).map((item) => (
              <p key={item.id} className="text-muted-foreground text-sm">
                <span className="text-white/80">{labelFor(item)}</span> ({item.price}): {item.note}
              </p>
            ))}
          </div>
          <div className="mx-auto max-w-2xl space-y-2 text-center">
            <p className="text-muted-foreground text-sm">
              Your domain is always registered in your name, and exporting your content, contacts, and donor data is
              free, always. You can leave with everything. That is the point.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gray-900/50 px-8 py-12">
        <div ref={howItWorksRef} className="mx-auto max-w-3xl space-y-6 text-center">
          <h2 className="text-2xl font-bold text-white">How it works</h2>
          <div className="grid gap-6 text-left md:grid-cols-3">
            <div className="step-item space-y-2">
              <div className="text-primary text-3xl font-bold">1</div>
              <h3 className="font-semibold text-white">Strategy Call</h3>
              <p className="text-sm text-white/80">
                Free 15-minute call to understand your needs and pick the right capabilities.
              </p>
            </div>
            <div className="step-item space-y-2">
              <div className="text-primary text-3xl font-bold">2</div>
              <h3 className="font-semibold text-white">Build & Launch</h3>
              <p className="text-sm text-white/80">
                A $250 Launch build gets you live: custom design, your content, domain connected.
              </p>
            </div>
            <div className="step-item space-y-2">
              <div className="text-primary text-3xl font-bold">3</div>
              <h3 className="font-semibold text-white">Grow</h3>
              <p className="text-sm text-white/80">
                Turn on capabilities as you grow, and hand any of them to us whenever you want them off your plate.
              </p>
            </div>
          </div>
          <div className="pt-4">
            <div className="flex items-center justify-center gap-2 text-sm text-white/70">
              <ArrowRight className="text-primary h-4 w-4" />
              <span>Pay annually on any plan and get 2 months free.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form CTA */}
      <FugueContactForm />
    </div>
  )
}
